﻿/**
 * 促销倒计时 Web Component（`<sale-countdown>`）
 *
 * 通过 snippets/header-script.liquid 全站加载一次；snippet 只输出 DOM。
 *
 * Liquid 用法（推荐）：
 *   {% render 'sale-countdown-snippet', target_date: '2026/08/31/23/59/59' %}
 * 详见 snippets/sale-countdown-snippet.liquid 顶部注释。
 *
 * 手写 markup（需自带 .day-val / .hour-val / .min-val / .sec-val）：
 *   <sale-countdown data-target-date="2026/08/31/23/59/59" class="spring-sale-countdown-wrapper">
 *     ...
 *   </sale-countdown>
 *
 * 属性：
 *   data-target-date  YYYY/MM/DD[/HH/mm/ss]，本地时区解析
 *   data-countdown-state  运行时写入：running | expired | invalid
 *
 * 动态刷新（筛选后重新观察可见性）：
 *   document.dispatchEvent(new CustomEvent('spring-sale-countdowns:refresh', {
 *     detail: { root: optionalElement },
 *   }));
 */
(function () {
  "use strict";

  var TAG_NAME = "sale-countdown";

  if (!window.customElements || window.customElements.get(TAG_NAME)) return;

  var visibleComponents = new Set();
  var tickerId = null;

  function pad(value) {
    return String(value).padStart(2, "0");
  }

  function parseTargetDate(value) {
    if (typeof value !== "string") return null;

    var parts = value.trim().split("/");
    if (
      parts.length < 3 ||
      parts.length > 6 ||
      parts.some(function (part) {
        return !/^\d+$/.test(part);
      })
    ) {
      return null;
    }

    var values = [0, 1, 1, 0, 0, 0];
    parts.forEach(function (part, index) {
      values[index] = Number(part);
    });

    var year = values[0];
    var month = values[1];
    var day = values[2];
    var hour = values[3];
    var minute = values[4];
    var second = values[5];

    if (
      year < 1000 ||
      year > 9999 ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59 ||
      second < 0 ||
      second > 59
    ) {
      return null;
    }

    var date = new Date(year, month - 1, day, hour, minute, second);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getHours() !== hour ||
      date.getMinutes() !== minute ||
      date.getSeconds() !== second
    ) {
      return null;
    }

    return date.getTime();
  }

  function needsTicker() {
    if (document.hidden) return false;

    for (var component of visibleComponents) {
      if (component.isActive()) return true;
    }

    return false;
  }

  function stopTicker() {
    if (tickerId === null) return;
    window.clearInterval(tickerId);
    tickerId = null;
  }

  function tick() {
    var now = Date.now();
    visibleComponents.forEach(function (component) {
      component.update(now);
    });

    if (!needsTicker()) stopTicker();
  }

  function syncTicker() {
    if (!needsTicker()) {
      stopTicker();
      return;
    }

    if (tickerId === null) tickerId = window.setInterval(tick, 1000);
  }

  function setComponentVisibility(component, visible) {
    if (!component.isConnected) visible = false;

    if (visible) {
      visibleComponents.add(component);
      component.update(Date.now());
    } else {
      visibleComponents.delete(component);
    }

    syncTicker();
  }

  var intersectionObserver =
    typeof window.IntersectionObserver === "function"
      ? new window.IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              setComponentVisibility(entry.target, entry.isIntersecting);
            });
          },
          { rootMargin: "200px", threshold: 0 },
        )
      : null;

  function connect(component) {
    component.refresh();

    if (intersectionObserver) intersectionObserver.observe(component);
    else setComponentVisibility(component, true);
  }

  function disconnect(component) {
    visibleComponents.delete(component);
    if (intersectionObserver) intersectionObserver.unobserve(component);
    syncTicker();
  }

  function refreshWithin(root) {
    var components = [];

    if (root instanceof Element && root.matches(TAG_NAME)) components.push(root);
    if (root && typeof root.querySelectorAll === "function") {
      root.querySelectorAll(TAG_NAME).forEach(function (component) {
        components.push(component);
      });
    }

    components.forEach(function (component) {
      component.refresh();
      if (intersectionObserver) {
        intersectionObserver.unobserve(component);
        intersectionObserver.observe(component);
      }
    });

    syncTicker();
  }

  class SpringSaleCountdown extends HTMLElement {
    static get observedAttributes() {
      return ["data-target-date"];
    }

    constructor() {
      super();
      this.connected = false;
      this.endTime = null;
      this.elements = null;
      this.lastValues = null;
      this.reportedInvalidValue = null;
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;

      if (!this.hasAttribute("role")) this.setAttribute("role", "timer");
      if (!this.hasAttribute("aria-live")) this.setAttribute("aria-live", "off");
      if (!this.hasAttribute("aria-atomic")) this.setAttribute("aria-atomic", "true");

      connect(this);
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.connected = false;
      disconnect(this);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== "data-target-date" || oldValue === newValue) return;
      this.readTargetDate();
      if (this.connected) {
        this.update(Date.now());
        syncTicker();
      }
    }

    cacheElements() {
      this.elements = {
        days: this.querySelector(".day-val"),
        hours: this.querySelector(".hour-val"),
        minutes: this.querySelector(".min-val"),
        seconds: this.querySelector(".sec-val"),
      };
    }

    hasCompleteMarkup() {
      return Boolean(
        this.elements && this.elements.days && this.elements.hours && this.elements.minutes && this.elements.seconds,
      );
    }

    readTargetDate() {
      var value = this.getAttribute("data-target-date") || "";
      this.endTime = parseTargetDate(value);
      this.lastValues = null;

      if (this.endTime !== null) {
        this.reportedInvalidValue = null;
        return;
      }

      this.setCountdownState("invalid");
      if (value && this.reportedInvalidValue !== value) {
        this.reportedInvalidValue = value;
        console.warn("Spring Sale Countdown: invalid target date", value, this);
      }
    }

    isActive() {
      return this.endTime !== null && this.endTime > Date.now();
    }

    setCountdownState(state) {
      if (this.getAttribute("data-countdown-state") !== state) {
        this.setAttribute("data-countdown-state", state);
      }
    }

    renderValues(values) {
      if (!this.hasCompleteMarkup()) return;
      var unchanged =
        this.lastValues &&
        values.every(function (value, index) {
          return value === this.lastValues[index];
        }, this);
      if (unchanged) return;

      this.elements.days.textContent = pad(values[0]);
      this.elements.hours.textContent = pad(values[1]);
      this.elements.minutes.textContent = pad(values[2]);
      this.elements.seconds.textContent = pad(values[3]);
      this.lastValues = values;
    }

    update(now) {
      if (!this.hasCompleteMarkup()) return false;

      if (this.endTime === null) {
        this.renderValues([0, 0, 0, 0]);
        return false;
      }

      var distance = this.endTime - now;
      if (distance <= 0) {
        this.setCountdownState("expired");
        this.renderValues([0, 0, 0, 0]);
        return false;
      }

      var day = 24 * 60 * 60 * 1000;
      var hour = 60 * 60 * 1000;
      var minute = 60 * 1000;
      var days = Math.floor(distance / day);
      var hours = Math.floor((distance % day) / hour);
      var minutes = Math.floor((distance % hour) / minute);
      var seconds = Math.floor((distance % minute) / 1000);

      this.setCountdownState("running");
      this.renderValues([days, hours, minutes, seconds]);
      return true;
    }

    refresh() {
      this.cacheElements();
      this.readTargetDate();
      this.update(Date.now());
    }
  }

  window.customElements.define(TAG_NAME, SpringSaleCountdown);

  document.addEventListener("visibilitychange", function () {
    if (document.hidden) stopTicker();
    else {
      tick();
      syncTicker();
    }
  });

  document.addEventListener("spring-sale-countdowns:refresh", function (event) {
    var root = event.detail && event.detail.root;
    refreshWithin(root || document);
  });
})();
